// src/services/api.ts - ENHANCED VERSION (CLEANED)
import axios from 'axios';
import { API_URL, WS_URL } from '../constants/config'; 
import { logger } from '../utils/logger';

logger.init('API Service initialized');

// Re-export config values
export { API_URL, WS_URL };
 
// Get full image URL
export const getFullImageUrl = (imagePath: any): string => {
  if (!imagePath) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"%3E%3Crect width="300" height="300" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
  }

  // If it's already a full URL
  if (
    imagePath.startsWith('http://') ||
    imagePath.startsWith('https://')
  ) {
    return imagePath;
  }

  const baseUrl = API_URL.replace('/api', '');

  let fullUrl = '';

  // Handle different path formats
  if (imagePath.startsWith('/uploads')) {
    fullUrl = `${baseUrl}${imagePath}`;
  } else if (imagePath.startsWith('uploads/')) {
    fullUrl = `${baseUrl}/${imagePath}`;
  } else if (imagePath.startsWith('/')) {
    fullUrl = `${baseUrl}${imagePath}`;
  } else if (imagePath.startsWith('data:image')) {
    // This is a base64 image preview - return as is for preview
    return imagePath;
  } else {
    // For old images without path prefix,
    // assume they're in products folder
    fullUrl = `${baseUrl}/uploads/products/${imagePath}`;
  }

  return fullUrl;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

api.interceptors.request.use(
  (config) => {
    logger.request(config.method?.toUpperCase() || 'GET', api.getUri(config));
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    logger.error('API Request Error', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    logger.response(response.status, response.config.url || '');
    return response;
  },
  (error) => {
    logger.error(`API Response Error: ${error.message}`);
    if (error.code === 'ERR_NETWORK') {
      logger.error(`Network Error - Make sure backend is running on ${API_URL}`);
    }
    // Handle 401 Unauthorized - token expired
    if (error.response?.status === 401) {
      logger.error('Token expired or invalid, logging out...');
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      // Optionally redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH API (Enhanced) ====================
export const register = (data: { name: string; email?: string; phone?: string; password: string }) => {
  return api.post('/auth/register', data);
};

export const login = (credentials: { email?: string; phone?: string; password: string }) => {
  return api.post('/auth/login', credentials);
};

export const getCurrentUser = () => api.get('/auth/me');

// Profile Management
export const updateProfile = (data: { name?: string; phone?: string }) => {
  return api.put('/auth/profile', data);
};

// Password Management
export const changePassword = (data: { currentPassword: string; newPassword: string }) => {
  return api.put('/auth/change-password', data);
};

export const forgotPassword = async (identifier: string) => {
  return axios.post(`${API_URL}/auth/forgot-password`, {
    identifier
  });
};

export const resetPassword = async (
  identifier: string,
  verificationCode: string,
  newPassword: string
) => {
  return axios.post(`${API_URL}/auth/reset-password`, {
    identifier,
    verificationCode,
    newPassword
  });
};

// Notification Preferences
export const updateNotificationPreferences = (preferences: {
  email?: boolean;
  sms?: boolean;
  push?: boolean;
  orderUpdates?: boolean;
  promotions?: boolean;
}) => {
  return api.put('/auth/notifications', preferences);
};

// Account Management
export const deleteAccount = () => {
  return api.delete('/auth/account');
};

// ==================== CHAT API ====================
export const getConversations = () => api.get('/chat/conversations');
export const getMessages = (partnerId: string) => api.get(`/chat/messages/${partnerId}`);
export const sendMessage = (to: string, message: string) => api.post('/chat/send', { to, message });
export const getUnreadCount = () => api.get('/chat/unread/count');

// ==================== ADMIN API ====================

// ----------------------------------------------------
// PRODUCTS
// ----------------------------------------------------

export interface AdminProductQuery {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  stock?: string;
  featured?: string;
}

export const adminGetProducts = (params: AdminProductQuery = {}) => {
  return api.get('/admin/products', {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      search: params.search ?? '',
      category: params.category ?? 'all',
      stock: params.stock ?? 'all',
      featured: params.featured ?? 'all',
    },
  });
};

export const adminCreateProduct = (data: any) =>
  api.post('/admin/products', data);

export const adminUpdateProduct = (
  id: string,
  data: any
) =>
  api.put(`/admin/products/${id}`, data);

export const adminDeleteProduct = (id: string) =>
  api.delete(`/admin/products/${id}`);


// ----------------------------------------------------
// ORDERS
// ----------------------------------------------------

export interface AdminOrderQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  paymentStatus?: string;
  startDate?: string;
  endDate?: string;
}

export const adminGetAllOrders = (
  params: AdminOrderQuery = {}
) => {
  return api.get('/admin/orders/all', {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      search: params.search ?? '',
      status: params.status ?? 'all',
      paymentStatus: params.paymentStatus ?? 'all',
      startDate: params.startDate ?? '',
      endDate: params.endDate ?? '',
    },
  });
};

export const adminUpdateOrderStatus = (
  id: string,
  status: string
) =>
  api.put(`/admin/orders/${id}/status`, { status });


// ----------------------------------------------------
// USERS
// ----------------------------------------------------

// Do NOT use this to load the entire customer database.
// Customer lookup should use the dedicated search endpoint.
export const adminGetAllUsers = () =>
  api.get('/admin/users/all');


// Customer lookup
export const adminSearchCustomers = (searchTerm: string) => {
  return api.get('/admin/customers/search', {
    params: {
      q: searchTerm.trim(),
    },
  });
};

// ========== NEW FUNCTIONS FOR USER DETAILS ==========
// Get user's order history (admin only)
export const getUserOrderHistory = async (userId: string) => {
  const token = localStorage.getItem('token');
  return axios.get(`${API_URL}/admin/users/${userId}/orders`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

// Get user's points history (admin only)
export const getUserPointsHistory = async (userId: string) => {
  const token = localStorage.getItem('token');
  return axios.get(`${API_URL}/admin/users/${userId}/points`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};
// ========== END OF NEW FUNCTIONS ==========

// Order statistics and counts
export const getPendingOrdersCount = async () => {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_URL}/admin/orders/pending-count`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response;
};

export const getOrderStats = async () => {
  const token = localStorage.getItem('token');
  return axios.get(`${API_URL}/admin/orders/stats`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

// ==================== IMAGE UPLOAD ====================
export const uploadImage = async (formData: FormData, category: string = 'products') => {
  const token = localStorage.getItem('token');
  
  // Append category to formData if not already present
  if (!formData.has('category')) {
    formData.append('category', category);
  }
  
  const response = await axios.post(`${API_URL}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${token}`
    }
  });
  return response;
};

// ==================== ADDRESS API ====================
export const getAddresses = () => api.get('/addresses');
export const createAddress = (data: any) => api.post('/addresses', data);
export const updateAddress = (id: string, data: any) => api.put(`/addresses/${id}`, data);
export const deleteAddress = (id: string) => api.delete(`/addresses/${id}`);
export const setDefaultAddress = (id: string) => api.put(`/addresses/${id}/default`);

// ==================== POINTS API ====================
export const getPointsBalance = () => api.get('/points/balance');
export const getPointsHistory = () => api.get('/points/history');
export const redeemPoints = (points: number) => api.post('/points/redeem', { points });

// ==================== ORDER API ====================
export const createOrder = (orderData: any) => api.post('/orders', orderData);
export const getUserOrders = () => api.get('/orders/my-orders');
export const getOrderDetails = (id: string) => api.get(`/orders/${id}`);
export const cancelOrder = (id: string) => api.put(`/orders/${id}/cancel`);

// ==================== SUPPORT CHAT API ====================
export const getSupportChats = () => api.get('/support/chats');
export const sendSupportMessage = (message: string) => api.post('/support/message', { message });
export const markSupportMessagesRead = (chatId: string) => api.put(`/support/${chatId}/read`);

export default api;